import { supabase } from './supabase';

export const SERVICE_HOUR_RATE_USD = 100;

export const SERVICE_HOUR_RATE_LABEL =
  (typeof import.meta !== 'undefined' &&
    import.meta.env?.VITE_SERVICE_HOUR_RATE_LABEL) ||
  `$${SERVICE_HOUR_RATE_USD}/hr`;

export type ServiceHourBalance = {
  hours_balance: number;
  total_hours_purchased: number;
  total_hours_used: number;
  updated_at: string | null;
};

export type ServiceHourRetainer = {
  quantity: number;
  status: string;
  monthly_usd: number;
};

export type ServiceHourTransaction = {
  id: string;
  hours: number;
  balance_after: number;
  transaction_type: 'purchase' | 'retainer_grant' | 'admin_adjust' | 'work_log' | string;
  note: string | null;
  engagement_id: string | null;
  created_at: string;
  created_by: string | null;
};

export type ServiceEngagement = {
  id: string;
  title: string;
  kind: 'setup' | 'seo' | 'other' | string;
  status: string;
  estimated_hours: number | null;
  quoted_hours: number | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
};

export type ServiceHoursSnapshot = {
  balance: ServiceHourBalance;
  retainer: ServiceHourRetainer;
  rate_usd: number;
  transactions: ServiceHourTransaction[];
  engagements: ServiceEngagement[];
};

async function invokeServiceHours<T = Record<string, unknown>>(
  body: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await supabase.functions.invoke('service-hours', { body });
  if (error) {
    throw new Error(error.message || 'service-hours request failed');
  }
  if (data && typeof data === 'object' && 'error' in data && data.error) {
    throw new Error(String((data as { error: string }).error));
  }
  return data as T;
}

export async function fetchServiceHoursSnapshot(
  businessId: string,
): Promise<ServiceHoursSnapshot> {
  return invokeServiceHours<ServiceHoursSnapshot>({
    action: 'get-balance',
    businessId,
  });
}

export async function adminCreateEngagement(input: {
  businessId: string;
  title: string;
  kind?: 'setup' | 'seo' | 'other';
  quotedHours?: number;
  estimatedHours?: number;
  notes?: string;
}) {
  return invokeServiceHours<{ engagement: ServiceEngagement }>({
    action: 'admin-create-engagement',
    ...input,
  });
}

export async function adminSendPurchase(input: {
  businessId: string;
  hours: number;
  title?: string;
  kind?: 'setup' | 'seo' | 'other';
  note?: string;
  engagementId?: string;
  sendInvoice?: boolean;
  successUrl?: string;
  cancelUrl?: string;
  daysUntilDue?: number;
}) {
  return invokeServiceHours<{
    success: boolean;
    method: 'invoice' | 'checkout';
    invoiceId?: string;
    invoiceUrl?: string | null;
    checkoutUrl?: string | null;
    hours: number;
    engagementId: string;
  }>({
    action: 'admin-send-purchase',
    ...input,
  });
}

export async function adminLogWork(input: {
  businessId: string;
  hours: number;
  note?: string;
  engagementId?: string;
  completeEngagement?: boolean;
}) {
  return invokeServiceHours<{ success: boolean; hours_balance: number }>({
    action: 'admin-log-work',
    ...input,
  });
}

export async function adminAdjustHours(input: {
  businessId: string;
  hours: number;
  note?: string;
}) {
  return invokeServiceHours<{ success: boolean; hours_balance: number }>({
    action: 'admin-adjust',
    ...input,
  });
}

export async function purchaseServiceHours(input: {
  businessId: string;
  hours: number;
  successUrl: string;
  cancelUrl: string;
}) {
  return invokeServiceHours<{
    success: boolean;
    checkoutUrl: string | null;
    sessionId: string;
    hours: number;
  }>({
    action: 'purchase-hours',
    ...input,
  });
}

export async function setServiceHourRetainer(input: {
  businessId: string;
  quantity: number;
}) {
  return invokeServiceHours<{
    success: boolean;
    quantity: number;
    monthly_usd: number;
  }>({
    action: 'set-retainer',
    ...input,
  });
}

export async function updateEngagementStatus(input: {
  engagementId: string;
  status: string;
}) {
  return invokeServiceHours<{ success: boolean }>({
    action: 'update-engagement-status',
    ...input,
  });
}

export function formatHours(n: number): string {
  const v = Math.round(n * 100) / 100;
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}
